/**
 * Pure helpers for building SearchableCard options from test location rows.
 *
 * @module siteDetailsFieldOptionsHelper
 */
import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import {
  TestLocationAddressEntry,
  TestLocationDetail,
} from "../../../api/services/siteNpiDetails/siteNpiDetailsTypes";
import {
  SiteDetailsFieldOptions,
  SITE_DETAILS_FIELDS,
} from "./siteDetailsForm.config";
import {
  blankIfMissingSiteDetailsPlaceholder,
  SiteDetailsFormValues,
} from "./siteDetailsForm.types";

/**
 * Reads one form field value from a test location row.
 *
 * For `alias`, always returns an empty string because API rows store
 * `string[]` aliases; dropdown options are built via
 * {@link collectUniqueAliasValues} instead. A `"-"` placeholder is returned
 * as an empty string so it is not treated as a real site name or identifier.
 *
 * @param location - Test location row from the Redux slice.
 * @param fieldKey - Site details form field key.
 * @returns Trimmed string value for the field, or empty when missing.
 */
export const getTestLocationFieldValue = (
  location: TestLocationDetail,
  fieldKey: keyof SiteDetailsFormValues,
): string => {
  if (fieldKey === "site_name") {
    return blankIfMissingSiteDetailsPlaceholder(
      location.site_name ?? location.name ?? "",
    );
  }

  if (fieldKey === "alias") {
    return "";
  }

  const rawValue = location[fieldKey];
  return typeof rawValue === "string"
    ? blankIfMissingSiteDetailsPlaceholder(rawValue)
    : "";
};

/**
 * Builds sorted SearchableCard options from a deduplicated value set.
 *
 * @param uniqueValues - Distinct option values for one field.
 * @returns `{ label, value }` options sorted alphabetically by label.
 */
const buildSearchableCardOptionsFromUniqueValues = (
  uniqueValues: Set<string>,
): SearchableCardOption[] =>
  Array.from(uniqueValues)
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({
      label: value,
      value,
    }));

/**
 * Collects unique trimmed alias strings from all test location rows.
 *
 * @param locations - Cached test location rows from Redux.
 * @returns Set of every alias value across all records.
 */
const collectUniqueAliasValues = (
  locations: TestLocationDetail[],
): Set<string> => {
  const uniqueValues = new Set<string>();

  locations.forEach((location) => {
    location.alias?.forEach((alias) => {
      const trimmed = blankIfMissingSiteDetailsPlaceholder(alias ?? "");
      if (trimmed) {
        uniqueValues.add(trimmed);
      }
    });
  });

  return uniqueValues;
};

/**
 * Builds deduplicated SearchableCard options for one site details field.
 *
 * For `alias`, flattens every alias from all location rows into one
 * deduplicated option list; other fields use one value per row.
 *
 * @param fieldKey - Site details form field key.
 * @param locations - Cached test location rows from Redux.
 * @returns `{ label, value }` options sorted alphabetically by label.
 */
export const buildSearchableCardOptionsForSiteDetailsField = (
  fieldKey: keyof SiteDetailsFormValues,
  locations: TestLocationDetail[],
): SearchableCardOption[] => {
  if (fieldKey === "alias") {
    return buildSearchableCardOptionsFromUniqueValues(
      collectUniqueAliasValues(locations),
    );
  }

  const uniqueValues = new Set<string>();

  locations.forEach((location) => {
    const value = getTestLocationFieldValue(location, fieldKey);
    if (value) {
      uniqueValues.add(value);
    }
  });

  return buildSearchableCardOptionsFromUniqueValues(uniqueValues);
};

/** Fields that get SearchableCard options from cached locations. */
const OPTION_FIELDS: ReadonlySet<keyof SiteDetailsFormValues> = new Set([
  "alias",
  "npi",
  "site_name",
]);

/**
 * Builds SearchableCard options for the site details fields that have them
 * (alias, npi, site_name). Address, city, state, and zip are populated
 * dynamically per selected NPI via {@link buildAddressFieldOptionsForNpi}.
 * Name options stay the distinct site names across every location, same as NPI.
 *
 * @param locations - Cached test location rows from Redux.
 * @returns Per-field option map keyed by {@link SiteDetailsFormValues}.
 */
export const buildSiteDetailsFieldOptions = (
  locations: TestLocationDetail[],
): SiteDetailsFieldOptions =>
  SITE_DETAILS_FIELDS.reduce<SiteDetailsFieldOptions>((options, field) => {
    if (!OPTION_FIELDS.has(field.key)) {
      return options;
    }
    options[field.key] = buildSearchableCardOptionsForSiteDetailsField(
      field.key,
      locations,
    );
    return options;
  }, {});

/** Address fields that are derived from `address_details` entries. */
const ADDRESS_FIELDS: ReadonlySet<keyof TestLocationAddressEntry> = new Set([
  "address",
  "city",
  "state",
  "zip",
]);

/**
 * Builds address/city/state/zip options for one selected NPI.
 *
 * Collects every `address_details` entry from all location rows whose NPI
 * matches the selection, then deduplicates each address field across the
 * collected entries. Returns an empty map when no NPI is selected or no
 * matching location has `address_details`.
 *
 * @param npi - NPI currently selected in the form (may be empty).
 * @param locations - Cached test location rows from Redux.
 * @returns Options for the address fields, or an empty map.
 */
export const buildAddressFieldOptionsForNpi = (
  npi: string,
  locations: TestLocationDetail[],
): SiteDetailsFieldOptions => {
  const selectedNpi = npi.trim();
  if (!selectedNpi) {
    return {};
  }

  const entries: TestLocationAddressEntry[] = [];
  locations.forEach((location) => {
    if ((location.npi ?? "").trim() !== selectedNpi) {
      return;
    }
    location.address_details?.forEach((entry) => {
      if (entry) {
        entries.push(entry);
      }
    });
  });

  if (entries.length === 0) {
    return {};
  }

  const options: SiteDetailsFieldOptions = {};
  ADDRESS_FIELDS.forEach((fieldKey) => {
    const uniqueValues = new Set<string>();
    entries.forEach((entry) => {
      const value = entry[fieldKey];
      if (typeof value !== "string") {
        return;
      }
      const trimmed = blankIfMissingSiteDetailsPlaceholder(value);
      if (trimmed) {
        uniqueValues.add(trimmed);
      }
    });
    if (uniqueValues.size > 0) {
      options[fieldKey] =
        buildSearchableCardOptionsFromUniqueValues(uniqueValues);
    }
  });

  return options;
};

/**
 * Returns whether the selected NPI has more than one address to choose from.
 *
 * Uses the same options as the address dropdowns. The NPI counts as having
 * multiple addresses when any of address, city, state, or zip has more than
 * one distinct value in `address_details`.
 *
 * @param npi - NPI currently selected in the form (may be empty).
 * @param locations - Cached test location rows from Redux.
 * @returns True when the selected NPI offers more than one address choice.
 */
export const selectedNpiHasMultipleAddresses = (
  npi: string,
  locations: TestLocationDetail[],
): boolean => {
  const options = buildAddressFieldOptionsForNpi(npi, locations);
  return [...ADDRESS_FIELDS].some((field) => (options[field]?.length ?? 0) > 1);
};
